import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { CopyBtn } from '../components/CopyBtn'
import { ReviewPager } from '../components/ReviewTimeline'
import { api, formatVnd, type CardOptions, type CardRow, type Deposit } from '../lib/api'
import { StatusBadge, formatDateTime, usePaged, type Tone } from './AccountPage'

const DEPOSIT_STATUS: Record<string, [string, Tone]> = {
  chua_thanh_toan: ['Chờ thanh toán', 'warn'],
  da_thanh_toan: ['Thành công', 'ok'],
  huy_bo: ['Đã hủy', 'danger'],
}

const CARD_STATUS: Record<string, [string, Tone]> = {
  thanhcong: ['Thành công', 'ok'],
  choduyet: ['Chờ duyệt', 'warn'],
  thatbai: ['Thất bại', 'danger'],
}

const PRESETS = [50000, 100000, 200000, 500000]

function Wallets() {
  const { user } = useAuth()
  if (!user) return null
  return (
    <div className="dep-wallets">
      <span>
        <i className="fa-solid fa-coins" aria-hidden /> {user.balance.toLocaleString('vi-VN')} xu
      </span>
      <span>
        <i className="fa-regular fa-gem" aria-hidden /> {user.balance2.toLocaleString('vi-VN')} gem
      </span>
    </div>
  )
}

function BankTab() {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const history = usePaged<Deposit>((p) => api.deposits(p))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    const value = Number(amount.replace(/\D/g, ''))
    if (!value || value < 10000) {
      setErr('Nạp tối thiểu 10.000đ')
      return
    }
    setBusy(true)
    try {
      const r = await api.createDeposit(value)
      navigate(`/deposit/${r.deposit.id}`)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <form className="dep-form" onSubmit={submit}>
        <div className="dep-field">
          <label htmlFor="dep-amount">Số tiền nạp (gem)</label>
          <div className="dep-presets">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`dep-preset${Number(amount.replace(/\D/g, '')) === p ? ' active' : ''}`}
                onClick={() => setAmount(p.toLocaleString('vi-VN'))}
              >
                {p.toLocaleString('vi-VN')}
              </button>
            ))}
          </div>
          <input
            id="dep-amount"
            inputMode="numeric"
            placeholder="Tối thiểu 10.000"
            value={amount}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '')
              setAmount(digits ? Number(digits).toLocaleString('vi-VN') : '')
            }}
          />
        </div>
        {err ? <p className="note">{err}</p> : null}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          <i className="fa-solid fa-file-invoice-dollar" aria-hidden /> {busy ? 'Đang tạo…' : 'Tạo phiếu nạp'}
        </button>
      </form>

      <div className="dep-history">
        <h2 className="dep-h2">Lịch sử nạp</h2>
        {history.loading && !history.items.length ? <p className="muted">Đang tải…</p> : null}
        {!history.loading && !history.items.length ? <p className="muted">Chưa có phiếu nạp.</p> : null}
        {history.items.length ? (
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nội dung CK</th>
                  <th>Số tiền</th>
                  <th>Thực nhận</th>
                  <th>Ngày tạo</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((d) => (
                  <tr key={d.id}>
                    <td>{d.id}</td>
                    <td>
                      <code>{d.code}</code>
                    </td>
                    <td>{d.amount.toLocaleString('vi-VN')}</td>
                    <td>{d.received != null ? d.received.toLocaleString('vi-VN') : '—'}</td>
                    <td className="acc-nowrap">{formatDateTime(d.created_at)}</td>
                    <td>
                      {d.status === 'chua_thanh_toan' ? (
                        <Link className="acc-action acc-action-warn" to={`/deposit/${d.id}`}>
                          <i className="fa-solid fa-qrcode" aria-hidden /> Thanh toán
                        </Link>
                      ) : (
                        <StatusBadge status={d.status} map={DEPOSIT_STATUS} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <ReviewPager page={history.page} totalPages={history.totalPages} disabled={history.loading} onPage={history.setPage} />
      </div>
    </>
  )
}

function CardTab() {
  const [opts, setOpts] = useState<CardOptions | null>(null)
  const [telco, setTelco] = useState('')
  const [amount, setAmount] = useState('')
  const [serial, setSerial] = useState('')
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [reload, setReload] = useState(0)
  const history = usePaged<CardRow>((p) => api.cards(p), reload)

  useEffect(() => {
    api
      .cardOptions()
      .then((r) => setOpts(r))
      .catch(() => setOpts({ open: false, telcos: [], amounts: [] }))
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    if (!telco) return setMsg({ ok: false, text: 'Vui lòng chọn nhà mạng' })
    if (!amount) return setMsg({ ok: false, text: 'Vui lòng chọn mệnh giá' })
    setBusy(true)
    try {
      const r = await api.submitCard({ telco, amount: Number(amount), serial, pin })
      setMsg({ ok: true, text: r.message })
      setSerial('')
      setPin('')
      setReload((n) => n + 1)
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  const closed = opts !== null && !opts.open

  return (
    <>
      <form className="dep-form" onSubmit={submit}>
        <p className="dep-rate">1.000đ mệnh giá = 1.000 xu · Sai mệnh giá có thể không hoàn thẻ.</p>
        {closed ? <p className="note">Nạp thẻ đang bảo trì, vui lòng nạp bằng chuyển khoản.</p> : null}
        <div className="dep-grid">
          <label>
            Loại thẻ
            <select value={telco} onChange={(e) => setTelco(e.target.value)} disabled={closed}>
              <option value="">Chọn nhà mạng</option>
              {opts?.telcos.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Mệnh giá
            <select value={amount} onChange={(e) => setAmount(e.target.value)} disabled={closed}>
              <option value="">Chọn mệnh giá</option>
              {opts?.amounts.map((a) => (
                <option key={a} value={a}>
                  {a.toLocaleString('vi-VN')}
                </option>
              ))}
            </select>
          </label>
          <label>
            Số seri
            <input value={serial} onChange={(e) => setSerial(e.target.value)} disabled={closed} required />
          </label>
          <label>
            Mã thẻ
            <input value={pin} onChange={(e) => setPin(e.target.value)} disabled={closed} required />
          </label>
        </div>
        {msg ? <p className={msg.ok ? 'dep-ok' : 'note'}>{msg.text}</p> : null}
        <button className="btn btn-primary" type="submit" disabled={busy || closed}>
          <i className="fa-solid fa-sim-card" aria-hidden /> {busy ? 'Đang gửi…' : 'Nạp thẻ'}
        </button>
      </form>

      <div className="dep-history">
        <h2 className="dep-h2">Lịch sử nạp thẻ</h2>
        {history.loading && !history.items.length ? <p className="muted">Đang tải…</p> : null}
        {!history.loading && !history.items.length ? <p className="muted">Chưa có thẻ nào.</p> : null}
        {history.items.length ? (
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Seri</th>
                  <th>Mã thẻ</th>
                  <th>Mệnh giá</th>
                  <th>Loại</th>
                  <th>Thời gian</th>
                  <th>Trạng thái</th>
                  <th>Ghi chú</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>
                      <code>{c.serial}</code>
                    </td>
                    <td>
                      <code>{c.pin}</code>
                    </td>
                    <td>{c.amount.toLocaleString('vi-VN')}</td>
                    <td>{c.telco}</td>
                    <td className="acc-nowrap">{formatDateTime(c.created_at)}</td>
                    <td>
                      <StatusBadge status={c.status} map={CARD_STATUS} />
                    </td>
                    <td>{c.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <ReviewPager page={history.page} totalPages={history.totalPages} disabled={history.loading} onPage={history.setPage} />
      </div>
    </>
  )
}

export function DepositPage() {
  const { user, loading } = useAuth()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'card' ? 'card' : 'bank'
  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />

  return (
    <div className="dep">
      <div className="dep-head">
        <h1 className="page-title">Nạp tiền vào ví</h1>
        <Wallets />
      </div>
      <div className="dep-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'bank'}
          className={`dep-tab${tab === 'bank' ? ' active' : ''}`}
          onClick={() => setParams({})}
        >
          <i className="fa-solid fa-building-columns" aria-hidden /> Chuyển khoản
          <small>Cộng vào ví gem</small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'card'}
          className={`dep-tab${tab === 'card' ? ' active' : ''}`}
          onClick={() => setParams({ tab: 'card' })}
        >
          <i className="fa-solid fa-sim-card" aria-hidden /> Thẻ cào
          <small>Cộng vào ví xu</small>
        </button>
      </div>
      <section className="panel dep-panel">{tab === 'card' ? <CardTab /> : <BankTab />}</section>
    </div>
  )
}

export function DepositPayPage() {
  const { id } = useParams()
  const { user, loading, refresh } = useAuth()
  const [dep, setDep] = useState<Deposit | null>(null)
  const [err, setErr] = useState('')
  const lastStatus = useRef<string | null>(null)

  useEffect(() => {
    if (!id || !user) return
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const load = () =>
      api
        .deposit(Number(id))
        .then((r) => {
          if (!alive) return
          if (lastStatus.current === 'chua_thanh_toan' && r.deposit.status === 'da_thanh_toan') {
            void refresh()
          }
          lastStatus.current = r.deposit.status
          setDep(r.deposit)
          if (r.deposit.status === 'chua_thanh_toan') timer = setTimeout(load, 10000)
        })
        .catch((e) => alive && setErr(e.message))
    load()
    return () => {
      alive = false
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id])

  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />
  if (err) return <p className="note">{err}</p>
  if (!dep) return <p className="muted">Đang tải…</p>

  return (
    <div className="dep">
      <Link className="okey-back" to="/deposit">
        <i className="fa-solid fa-arrow-left" aria-hidden /> Tạo phiếu khác
      </Link>
      <div className="dep-head">
        <h1 className="page-title">Phiếu nạp #{dep.id}</h1>
        <StatusBadge status={dep.status} map={DEPOSIT_STATUS} />
      </div>

      {dep.status === 'da_thanh_toan' ? (
        <div className="panel dep-done">
          <i className="fa-solid fa-circle-check" aria-hidden />
          <h2>Thanh toán thành công</h2>
          <p>
            Đã cộng <strong>{(dep.received ?? dep.amount).toLocaleString('vi-VN')} gem</strong> vào ví.
          </p>
          <Link className="btn btn-primary" to="/mua-minecraft">
            Mua key Minecraft
          </Link>
        </div>
      ) : null}

      {dep.status === 'chua_thanh_toan'
        ? dep.banks?.map((b) => (
            <div key={b.id} className="panel dep-pay">
              {b.qr_url ? (
                <div className="dep-qr">
                  <img src={b.qr_url} alt="VietQR" loading="lazy" referrerPolicy="no-referrer" />
                  <p className="muted">Mở app ngân hàng & quét mã</p>
                </div>
              ) : null}
              <div className="dep-info">
                <h2>
                  {b.icon ? <img src={b.icon} alt="" /> : null}
                  {b.name}
                </h2>
                <dl>
                  <div>
                    <dt>Số tiền</dt>
                    <dd>
                      <strong>{formatVnd(dep.amount)}</strong>
                      <CopyBtn text={String(dep.amount)} />
                    </dd>
                  </div>
                  <div>
                    <dt>Nội dung CK</dt>
                    <dd>
                      <code className="dep-code">{dep.code}</code>
                      <CopyBtn text={dep.code} />
                    </dd>
                  </div>
                  <div>
                    <dt>Số tài khoản</dt>
                    <dd>
                      {b.account_no}
                      <CopyBtn text={b.account_no} />
                    </dd>
                  </div>
                  <div>
                    <dt>Chủ tài khoản</dt>
                    <dd>{b.account_name}</dd>
                  </div>
                </dl>
                <p className="muted dep-wait">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Đang chờ chuyển khoản…
                </p>
              </div>
            </div>
          ))
        : null}

      {dep.status === 'huy_bo' ? <p className="note">Phiếu nạp đã hủy.</p> : null}
    </div>
  )
}
